import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

export interface TtsConfig {
  stability: number;
  similarityBoost: number;
  style: number;
  speed: number;
  useSpeakerBoost: boolean;
}

export const DEFAULT_TTS_CONFIG: TtsConfig = {
  stability: 0.6,
  similarityBoost: 0.75,
  style: 0.05,
  speed: 0.93,
  useSpeakerBoost: true,
};

interface TtsConfigPanelProps {
  ttsConfig: TtsConfig;
  onTtsConfigChange: (config: TtsConfig) => void;
}

const SLIDER_FIELDS: Array<{
  key: keyof Pick<TtsConfig, 'stability' | 'similarityBoost' | 'style' | 'speed'>;
  label: string;
  description: string;
  min: number;
  max: number;
  step: number;
}> = [
  {
    key: 'stability',
    label: 'Stability',
    description:
      'Determines voice stability and randomness between each generation. Lower values introduce broader emotional range. Higher values can result in a monotonous voice with limited emotion. (Default: 0.5)',
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: 'similarityBoost',
    label: 'Similarity Boost',
    description:
      'Determines how closely the AI adheres to the original voice when attempting to replicate it. Higher values produce a closer match. (Default: 0.75)',
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: 'style',
    label: 'Style',
    description:
      'Amplifies the style of the original speaker. Consumes additional computational resources and increases latency if set to anything other than 0. (Default: 0.0)',
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: 'speed',
    label: 'Speed',
    description:
      'Adjusts the speed of the voice. 1.0 is the default speed. Values below 1.0 slow down the speech, above 1.0 speed it up. Extreme values near the boundaries may affect quality. (Default: 1.0)',
    min: 0.7,
    max: 1.2,
    step: 0.01,
  },
];

export function TtsConfigPanel({
  ttsConfig,
  onTtsConfigChange,
}: TtsConfigPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleSliderChange = (
    key: keyof Pick<TtsConfig, 'stability' | 'similarityBoost' | 'style' | 'speed'>,
    value: number,
  ) => {
    onTtsConfigChange({ ...ttsConfig, [key]: value });
  };

  return (
    <div className="w-full mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="text-sm font-semibold text-amber-800">
          Voice Settings (Tester Mode)
        </span>
        <ChevronDown
          className={`w-4 h-4 text-amber-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="mt-4 space-y-5">
          {SLIDER_FIELDS.map((field) => (
            <div key={field.key}>
              <div className="flex justify-between items-center mb-1">
                <Label className="text-xs font-medium text-slate-700">
                  {field.label}
                </Label>
                <span className="text-xs font-mono text-slate-500">
                  {ttsConfig[field.key].toFixed(2)}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 mb-2">
                {field.description}
              </p>
              <input
                type="range"
                min={field.min}
                max={field.max}
                step={field.step}
                value={ttsConfig[field.key]}
                onChange={(e) =>
                  handleSliderChange(field.key, parseFloat(e.target.value))
                }
                className="w-full h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                <span>{field.min}</span>
                <span>{field.max}</span>
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs font-medium text-slate-700">
                Speaker Boost
              </Label>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Boosts similarity to the original speaker. Requires slightly higher
                computational load, which increases latency. (Default: on)
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
              <input
                type="checkbox"
                checked={ttsConfig.useSpeakerBoost}
                onChange={(e) =>
                  onTtsConfigChange({
                    ...ttsConfig,
                    useSpeakerBoost: e.target.checked,
                  })
                }
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-600" />
            </label>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onTtsConfigChange(DEFAULT_TTS_CONFIG)}
            className="text-xs text-amber-700 border-amber-300 hover:bg-amber-100"
          >
            Reset to Defaults
          </Button>
        </div>
      )}
    </div>
  );
}
